    /**
 * Different rendering for (different types of) responses; to display in the chat page.
 */

    import React, { useState } from "react";
    import { useSource } from "../../contexts/SourceContext";
    import { SearchResultItem } from "./search_result";
    import { SourceMarker } from "./source_marker";
    import { SourceList } from "./source_list";
    import Markdown from "../ui-common/markdown";
    import { cleanWords } from "../../lib/utils";
    
    const ChatResponse = ({
        message
    }) => {
      const { setActiveSource } = useSource();
      const [openSourceIndices, setOpenSourceIndices] = useState(new Set());
    
      const handleSourceClick = (sourceNumber) => {
        setOpenSourceIndices(prev => {
          const newSet = new Set();
          if (!prev.has(sourceNumber)) {
            newSet.add(sourceNumber);
            setActiveSource({ messageId: message.id, number: sourceNumber });
          } else {
            setActiveSource(null);
          }
          // If the clicked source is already open, the newSet will be empty, effectively closing it.
          return newSet;
        });
      };
    
      const sources = message.quotes ? [...message.quotes] : [];
      const hasNotInContext = message.response && message.response.includes('(((not from context)))');
      if (hasNotInContext) {
        sources.push({ src_group: 'not_from_context' });
      }
      const notInContextSourceNumber = hasNotInContext ? sources.length : null;
    
      const renderResponse = () => {
          {/* ## Example for `message.response`: 
              This course is "Experimental Engineering Lab" focused on mechanical engineering metrology and measurements (((1))). The course teaches students how to design experiments and prepare for experiments (((2))). Topics covered include dynamic measurements (((3))), digital signal processing for measurements (((4))), analog-digital conversion, Fourier transforms, and static and dynamic calibration (((5))). Students perform laboratory work including dynamic oscillations experiments and will do beam oscillations in solids the following year (((6,7,8))). Also it is known that similar course is offered at MIT (((not from context))).
            ## As you can see, the response may contain references to the sources (in triple parentheses); sometimes the sources are grouped (e.g., 6,7,8).
        */}
        if (!message.response) {
          return null;
        }
    
        const regex = /\(\(\(([\d,]+|not from context)\)\)\)/g;
        const matches = [...message.response.matchAll(regex)];
        if (matches.length === 0) {
          return message.response;
        }
    
        const result = [];
        let lastIndex = 0;
    
        matches.forEach((match, matchIndex) => {
          const textBefore = message.response.substring(lastIndex, match.index);
          if (textBefore) {
            result.push(<Markdown key={`b-${matchIndex}`}>{textBefore}</Markdown>);
          }
    
          const sourceContent = match[1];
    
          /* handle according to the type of source reference */
          if (sourceContent === 'not from context') {
            result.push(<span key={`c-${matchIndex}`}>&nbsp;<SourceMarker messageId={message.id} sourceNumber={notInContextSourceNumber} onClick={handleSourceClick} isOpen={openSourceIndices.has(notInContextSourceNumber)} isNotInContext={true} /></span>);
          } else {
            const sourceNumbers = sourceContent.split(',').map(s => parseInt(s.trim(), 10));
            const sources = sourceNumbers.map((num, i) => {
              return (
                <React.Fragment key={`s-${matchIndex}-${i}`}>
                   <SourceMarker
                     messageId={message.id}
                     sourceNumber={num}
                     onClick={handleSourceClick}
                     isOpen={openSourceIndices.has(num)}
                     isNotInContext={false}
                   />
                  {i < sourceNumbers.length - 1 ? ' ' : ''  /* Space between multiple sources */}
                </React.Fragment>
              );
            });
            result.push(<span key={`c-${matchIndex}`}>&nbsp;{sources}&nbsp;</span>);
          }
    
          lastIndex = match.index + match[0].length;
        });
    
        const textAfter = message.response.substring(lastIndex);
        if (textAfter) {
          result.push(<Markdown key={`a-${matches.length}`}>{textAfter}</Markdown>);
        }
    
        return result;
      };
    
      return (
        <>
        <p className="text-sm whitespace-pre-wrap break-words">{renderResponse()}</p>
        {sources.length > 0 && (
            <SourceList sources={sources} openSourceIndices={openSourceIndices} handleSourceClick={handleSourceClick} messageId={message.id} />
        )}
            {/* legacy quotes is disabled; left for debugging purposes */}{false && message.quotes && message.quotes.length > 0 && (
            <div className="mt-2 pt-2 border-t border-border">
                <div className="flex items-center gap-2 mb-2">
                    <p className="text-xs font-medium text-muted-foreground">Related materials:</p>
                    </div>
                    <div className="space-y-2">
                    {message.quotes.map((quote, index) => (
                    <div key={index} className="text-xs bg-muted/30 p-2 rounded border">
                        {quote.text && (
                        <div className="mb-1">
                            <span className="font-medium">Quote ({index}):</span>
                            <Markdown className="inline">{quote.text}</Markdown>
                        </div>
                        )}
                        {quote.source && (
                        <div className="text-muted-foreground">
                            <span className="font-medium">Source ({index + 1}):</span> {quote.source}
                        </div>
                        )}
                    </div>
                    ))}
                </div>
            </div>
            )}
        </>
      );
    }
    
    const SearchResponse = ({
        message
    }) => {
    
        // process the query to keywords
        const keywords = React.useMemo(() => {
          return cleanWords(message.query.trim().split(/\s+/).filter(Boolean));
        }, [message.query]);
    
        return (
        <>
        <Markdown className="text-sm whitespace-pre-wrap break-words">{message.response}</Markdown>
            {message.quotes && message.quotes.length > 0 && (
                <div className="flex justify-center p-2 min-h-screen">
                    <div className="w-full max-w-2xl rounded-2xl">
                        {/* Results */}
                        <div>
                        {message.quotes.map((result, index) => (
                            <SearchResultItem key={index} result={result} index={index} keywords={keywords} />
                        ))}
                        </div>
                    </div>
                </div>
            )}
        </>
      );
    }  
    
    export { ChatResponse, SearchResponse };